import React, { FC } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'src/presentation/components/agent_desktop_components';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import Footer from 'src/presentation/components/forms/Footer';
import generateId from 'src/lib/generateId';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import RadioGroup from 'src/presentation/components/forms/RadioGroup';
import agentStatusReasonsResource, { AgentStatusType } from 'src/actions/agentStatus/agentStatusReasonsResource';

const AgentStatusEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const dispatch = useDispatch();

  const formContext = useForm({
    add: agentStatusReasonsResource.effects.add,
    defaultItem: { name: '' },
    errors,
    item,
    resetErrors: agentStatusReasonsResource.actions.resetErrors,
    root,
    onSave: ({ add, isNew, item, resetErrors, update, updateState, validate, workingStateRef }) => {
      updateState({ name: workingStateRef.current.name.trim() });

      dispatch(resetErrors());
      if (validate && !dispatch(validate(workingStateRef.current))) {
        return;
      }

      isNew
        ? dispatch(add({}, { id: generateId(), ...workingStateRef.current }))
        : dispatch(update({ id: item.id }, workingStateRef.current));
    },
    update: agentStatusReasonsResource.effects.update,
    validate: agentStatusReasonsResource.effects.validate,
  });
  const { onClose, onSave, isNew } = formContext;

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Create' : 'Edit'} Agent Status</H1>

      <Body data-aid="agentStatus-editor">
        <Section>
          <Row>
            <Input
              data-aid="agentStatus-input"
              attr="name"
              autoFocus
              label="Agent Status"
              placeholder="e.g. Coffee Break"
            />
          </Row>
          <RadioGroup
            attr="type"
            data-aid="type"
            description="An Agent Status can be one of two types. Active statuses are used by agents to indicate what they are currently working on within Gladly. Away statuses are used by agents when they need to step away from their computer or when they are working outside of Gladly. When an agent uses an Away status, information within Gladly is hidden for privacy."
            disabled={!isNew}
            options={[
              { label: 'Active', value: AgentStatusType.Active },
              { label: 'Away', value: AgentStatusType.Away },
            ]}
            label="Type"
          />
        </Section>
      </Body>

      <Footer>
        <Button data-aid="agentStatus-edit-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="agentStatus-edit-save" onClick={onSave}>
          {isNew ? 'Create Agent Status' : 'Save'}
        </Button>
      </Footer>
    </Form>
  );
};

export default AgentStatusEditor;
